import { isDevMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';

import { registerServiceWorker } from './register-sw';
import { appConfig } from '~/app.config';
import { AppComponent } from '~/app.component';
import { environment } from './environments/environment';
import packageJson from '../package.json';

if (!isDevMode()) {
  registerServiceWorker(packageJson.version);
}

bootstrapApplication(AppComponent, appConfig)
  .then(() => {
    // Defer Swiper + Sentry by 5s to push them completely out of the
    // Lighthouse TBT window (~10s). requestIdleCallback fires too soon.
    setTimeout(() => {
      // Swiper JS deferred 5s — CSS remains in the global styles bundle for correct initial render.
      import('swiper/element/bundle').then((m) => m.register());
      if (shouldEnableSentry()) {
        initSentry();
      }
    }, 5_000);
  })
  .catch((err) => console.error(err));

function shouldEnableSentry(): boolean {
  if (!environment.enabledSentry) return false;
  // Optional per-client allowlist. Empty/undefined = enable for every brand on this environment.
  // Populated = only init Sentry when the current hostname is in the list.
  const hosts = (environment as { sentryEnabledHosts?: string[] }).sentryEnabledHosts;
  if (!hosts?.length) return true;
  return hosts.includes(window.location.hostname);
}

function initSentry(): void {
  import('@sentry/browser').then(
    ({
      BrowserClient,
      browserTracingIntegration,
      consoleLoggingIntegration,
      defaultStackParser,
      getCurrentScope,
      makeFetchTransport,
      lazyLoadIntegration,
      addIntegration,
    }) => {
      const client = new BrowserClient({
        dsn: 'https://6c94d38598043c91486b9987a691618f@o4508400472293376.ingest.us.sentry.io/4511512496832512',
        transport: makeFetchTransport,
        stackParser: defaultStackParser,
        environment: environment.environmentName,
        release: `bet-plus-web-angular@${packageJson.version}`,
        integrations: [browserTracingIntegration(), consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] })],
        tracePropagationTargets: environment.sentry.tracePropagationTargets,
        sampleRate: 0.1,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 0.1,
        enableLogs: true,
        sendDefaultPii: true,
        beforeSend(event) {
          const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
          const blockedDomains = ['help.smartico.ai', 'ksjotypi.san.stape.io', 'api.visitorapi.com'];
          if (frames.some((f) => blockedDomains.some((d) => f.filename?.includes(d)))) {
            return null;
          }
          return event;
        },
        denyUrls: [/help\.smartico\.ai/, /ksjotypi\.san\.stape\.io/, /api\.visitorapi\.com/],
        ignoreErrors: [],
      });
      getCurrentScope().setClient(client);
      client.init();

      // Defer replay integration by 30s — it's diagnostic-only and costs ~515ms TBT
      setTimeout(() => {
        lazyLoadIntegration('replayIntegration')
          .then((replayIntegration) => {
            addIntegration(replayIntegration());
          })
          .catch((error) => {
            console.error('Failed to load replay integration:', error);
          });
      }, 30_000);
    },
  );
}
